import { Token } from '@/src/models';
import { xrpToDrops } from 'xrpl';

type CreateOrderAmountsProps = {
  orderType: 'buy' | 'sell';
  baseToken: Token | undefined;
  quoteToken: Token | undefined;
  quantity: string;
  total: string;
};

/**
 * Creates XRPL order amounts (takerGets and takerPays) for DEX trading.
 *
 * This helper function constructs the proper amount objects for XRPL OfferCreate transactions
 * based on the order type (buy/sell) and token types (XRP vs non-XRP tokens).
 *
 * @param orderType - Whether this is a 'buy' or 'sell' order
 * @param baseToken - The base token in the trading pair (e.g., XRP)
 * @param quoteToken - The quote token in the trading pair (e.g., USDC)
 * @param quantity - Amount of base token to trade
 * @param total - Total amount of quote token for the trade
 * @returns Object with takerGets and takerPays amounts formatted for XRPL
 *
 * @example
 * ```tsx
 * // Buy 100 XRP with 50 USDC
 * const amounts = createOrderAmounts({
 *   orderType: 'buy',
 *   baseToken: { code: 'XRP' },
 *   quoteToken: { code: 'USDC', issuer: 'rN7n7otQDd6FczFgLdSqtcsAUxDkw6fzRH' },
 *   quantity: '100',
 *   total: '50'
 * });
 * // Returns: {
 * //   takerGets: '50000000', // 50 USDC in drops
 * //   takerPays: { currency: 'XRP', issuer: '', value: '100' }
 * // }
 *
 * // Sell 100 XRP for 50 USDC
 * const amounts = createOrderAmounts({
 *   orderType: 'sell',
 *   baseToken: { code: 'XRP' },
 *   quoteToken: { code: 'USDC', issuer: 'rN7n7otQDd6FczFgLdSqtcsAUxDkw6fzRH' },
 *   quantity: '100',
 *   total: '50'
 * });
 * // Returns: {
 * //   takerGets: { currency: 'USDC', issuer: 'rN7n7otQDd6FczFgLdSqtcsAUxDkw6fzRH', value: '50' },
 * //   takerPays: '100000000' // 100 XRP in drops
 * // }
 * ```
 */
export const createOrderAmounts = ({
  orderType,
  baseToken,
  quoteToken,
  quantity,
  total,
}: CreateOrderAmountsProps) => {
  const createAmount = (token: Token | undefined, value: string) => {
    if (!token || !value) return '0';

    if (token.code === 'XRP') {
      return xrpToDrops(value);
    }

    return {
      currency: token.code,
      issuer: token.issuer || '',
      value,
    };
  };

  return {
    takerGets:
      orderType === 'buy' ? createAmount(quoteToken, total) : createAmount(baseToken, quantity),
    takerPays:
      orderType === 'buy' ? createAmount(baseToken, quantity) : createAmount(quoteToken, total),
  };
};
